import clevertap from 'clevertap-web-sdk';

const ACCOUNT_ID = process.env.REACT_APP_CLEVERTAP_ACCOUNT_ID || '';
const REGION = process.env.REACT_APP_CLEVERTAP_REGION || 'in1';
const ANALYTICS_ENABLED = String(process.env.REACT_APP_ENABLE_ANALYTICS || '').toLowerCase() === 'true';

let initialized = false;

function isReady() {
  return initialized && ANALYTICS_ENABLED && Boolean(ACCOUNT_ID);
}

function isNotNullAndBlank(value) {
  return value != null && String(value).trim() !== '';
}

/**
 * Mirrors Android createProfileData().
 * Maps app profile fields to CleverTap profile keys.
 */
function createProfileData(properties = {}, externalId, includeIdentity) {
  const profileUpdate = {};

  if (properties.userName != null || properties.username != null || properties.name != null) {
    profileUpdate.Name = properties.userName ?? properties.username ?? properties.name;
  }

  if (includeIdentity) {
    const identity =
      (isNotNullAndBlank(externalId) ? String(externalId).trim() : '') ||
      (isNotNullAndBlank(properties.userId) ? String(properties.userId).trim() : '') ||
      (isNotNullAndBlank(properties.cognitoId) ? String(properties.cognitoId).trim() : '') ||
      (isNotNullAndBlank(properties._id) ? String(properties._id).trim() : '') ||
      (isNotNullAndBlank(properties.id) ? String(properties.id).trim() : '');

    if (identity) {
      profileUpdate.Identity = identity;
    }
  }

  if (properties.email != null) {
    profileUpdate.Email = properties.email;
  }

  const phoneRaw = properties.phoneNumber ?? properties.phone ?? properties.mobile;
  if (phoneRaw != null && String(phoneRaw).trim() !== '') {
    const digits = String(phoneRaw).replace(/\D/g, '');
    const countryCode = String(properties.countryCode || properties.dialCode || '91').replace(/\D/g, '') || '91';
    profileUpdate.Phone = String(phoneRaw).startsWith('+')
      ? String(phoneRaw).trim()
      : `+${countryCode}${digits}`;
    profileUpdate['MSG-whatsapp'] = true;
  }

  if (properties.cognitoId != null) {
    profileUpdate.cognitoId = properties.cognitoId;
  }

  if (properties.rapCoinBalance != null) {
    profileUpdate.rapCoinBalance = properties.rapCoinBalance;
  }

  if (properties.totalCoins != null) {
    profileUpdate.totalCoins = properties.totalCoins;
  }

  if (properties.totalGems != null) {
    profileUpdate.totalGems = properties.totalGems;
  }

  if (properties.vipRoom != null) {
    profileUpdate.vipRoom = properties.vipRoom;
  }

  if (properties.isPremium != null) {
    profileUpdate.isPremium = properties.isPremium;
  }

  if (properties.isGuest != null) {
    profileUpdate.isGuest = properties.isGuest;
  }

  if (properties.totalXP != null || properties.totalXp != null || properties.xp_balance != null) {
    const xp = properties.totalXP ?? properties.totalXp ?? properties.xp_balance;
    profileUpdate.totalXP = xp;
    profileUpdate.xp_balance = xp;
  }

  if (properties.subscription_tier != null) {
    profileUpdate.subscription_tier = properties.subscription_tier;
  }

  if (properties.subscription_start_date != null) {
    profileUpdate.subscription_start_date = properties.subscription_start_date;
  }

  if (properties.subscription_expiry_date != null) {
    profileUpdate.subscription_expiry_date = properties.subscription_expiry_date;
  }

  if (properties.subscription_status != null) {
    profileUpdate.subscription_status = properties.subscription_status;
  }

  return profileUpdate;
}

function logAnalytics(message, payload) {
  if (process.env.NODE_ENV === 'production') return;
  if (payload !== undefined) {
    // eslint-disable-next-line no-console
    console.info(`[CleverTap] ${message}`, payload);
    return;
  }
  // eslint-disable-next-line no-console
  console.info(`[CleverTap] ${message}`);
}

/**
 * Initialize CleverTap Web SDK.
 * See: https://developer.clevertap.com/docs/web-quickstart-guide
 */
export function initCleverTap() {
  if (initialized) {
    return true;
  }

  if (!ANALYTICS_ENABLED) {
    logAnalytics('skipped init: REACT_APP_ENABLE_ANALYTICS is not true');
    return false;
  }

  if (!ACCOUNT_ID) {
    logAnalytics('skipped init: REACT_APP_CLEVERTAP_ACCOUNT_ID is missing');
    return false;
  }

  try {
    clevertap.privacy.push({ optOut: false });
    clevertap.privacy.push({ useIP: false });
    clevertap.spa = true;
    clevertap.init(ACCOUNT_ID, REGION);
    initialized = true;
    logAnalytics(`initialized account=${ACCOUNT_ID} region=${REGION}`);
    return true;
  } catch (error) {
    logAnalytics('init failed', error);
    return false;
  }
}

/**
 * Track a custom event with optional properties.
 * @param {string} eventName
 * @param {Record<string, string | number | boolean | Date>} [properties]
 */
export function trackEvent(eventName, properties) {
  if (!eventName) return;

  if (!isReady()) {
    logAnalytics(`skipped event "${eventName}" (not ready)`, {
      ANALYTICS_ENABLED,
      initialized,
      hasAccountId: Boolean(ACCOUNT_ID),
    });
    return;
  }

  const payload = properties && typeof properties === 'object'
    ? sanitizeEventProperties(properties)
    : undefined;

  if (payload) {
    clevertap.event.push(eventName, payload);
  } else {
    clevertap.event.push(eventName);
  }

  logAnalytics(`event "${eventName}"`, payload || {});
}

/**
 * CleverTap system Charged event for revenue.
 * @param {Record<string, unknown>} properties
 */
export function recordCharged(properties = {}) {
  if (!isReady()) {
    logAnalytics('skipped Charged event (not ready)');
    return;
  }
  const payload = sanitizeEventProperties(properties);
  clevertap.event.push('Charged', payload);
  logAnalytics('event "Charged"', payload);
}

function sanitizeEventProperties(properties) {
  const cleaned = {};

  Object.entries(properties).forEach(([key, value]) => {
    if (value === undefined) return;
    cleaned[key] = value;
  });

  return cleaned;
}

/**
 * Identify / login a user in CleverTap (matches Android identifyUser).
 * @param {Record<string, any>} profile
 * @param {string|null} [externalId]
 */
export function identifyUser(profile, externalId = null) {
  if (!isReady() || !profile) {
    logAnalytics('skipped identifyUser (not ready or no profile)');
    return;
  }

  const profileUpdate = createProfileData(profile, externalId, true);
  if (Object.keys(profileUpdate).length === 0) return;

  clevertap.onUserLogin.push({ Site: profileUpdate });
  logAnalytics('onUserLogin', profileUpdate);
}

/**
 * Update properties on the current CleverTap profile (matches Android setUserProperties).
 * @param {Record<string, any>} properties
 */
export function setUserProperties(properties = {}) {
  if (!isReady() || !properties || typeof properties !== 'object') return;

  const profileUpdate = createProfileData(properties, null, true);
  if (Object.keys(profileUpdate).length === 0) return;

  clevertap.profile.push({ Site: profileUpdate });
}

/** @deprecated Prefer setUserProperties to match Android naming. */
export function updateUserProfile(properties = {}) {
  setUserProperties(properties);
}

export function isCleverTapEnabled() {
  return isReady();
}
